import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  OutlinedInput,
  Alert,
  CircularProgress
} from '@mui/material';
import { useMeeting } from '../contexts/MeetingContext';

const AddMeeting = ({ open, onClose, meeting = null, onSubmit }) => {
  const { createMeeting, updateMeeting, loading } = useMeeting();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    meeting_date: '',
    location: '',
    agenda: '',
    participants: []
  });
  const [error, setError] = useState('');

  // Mock participants for demo (in real app, fetch from user API)
  const availableParticipants = [
    { id: 1, name: 'John Doe', email: 'john@example.com' },
    { id: 2, name: 'Jane Smith', email: 'jane@example.com' },
    { id: 3, name: 'Mike Johnson', email: 'mike@example.com' },
    { id: 4, name: 'Sarah Wilson', email: 'sarah@example.com' },
    { id: 5, name: 'David Brown', email: 'david@example.com' }
  ];

  useEffect(() => {
    if (meeting) {
      setFormData({
        title: meeting.title || '',
        description: meeting.description || '',
        meeting_date: meeting.meeting_date ? meeting.meeting_date.slice(0, 16) : '',
        location: meeting.location || '',
        agenda: meeting.agenda || '',
        participants: meeting.participants?.map(p => p.participant_id) || []
      });
    } else {
      setFormData({
        title: '',
        description: '',
        meeting_date: '',
        location: '',
        agenda: '',
        participants: []
      });
    }
    setError('');
  }, [meeting, open]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleParticipantChange = (event) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      participants: typeof value === 'string' ? value.split(',') : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.title.trim()) {
      setError('Meeting title is required');
      return;
    }
    if (!formData.meeting_date) {
      setError('Meeting date is required');
      return;
    }

    try {
      if (meeting) {
        // Update existing meeting
        await updateMeeting(meeting.id, formData);
      } else {
        // Create new meeting
        await createMeeting(formData);
      }

      if (onSubmit) {
        onSubmit(formData);
      }
      onClose();
    } catch (error) {
      setError(error.message || 'Failed to save meeting');
    }
  };

  const handleClose = () => {
    onClose();
    setError('');
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {meeting ? 'Edit Meeting' : 'Create New Meeting'}
      </DialogTitle>
      
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Meeting Title"
                required
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Meeting Date & Time"
                type="datetime-local"
                required
                value={formData.meeting_date}
                onChange={(e) => handleChange('meeting_date', e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Location"
                value={formData.location}
                onChange={(e) => handleChange('location', e.target.value)}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Participants</InputLabel>
                <Select
                  multiple
                  value={formData.participants}
                  onChange={handleParticipantChange}
                  input={<OutlinedInput label="Participants" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const participant = availableParticipants.find(p => p.id === value);
                        return (
                          <Chip 
                            key={value} 
                            label={participant?.name || value} 
                            size="small" 
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {availableParticipants.map((participant) => (
                    <MenuItem key={participant.id} value={participant.id}>
                      {participant.name} ({participant.email})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Agenda"
                multiline
                rows={4}
                value={formData.agenda}
                onChange={(e) => handleChange('agenda', e.target.value)}
                variant="outlined"
                placeholder="Enter meeting agenda items..."
              />
            </Grid>

            {meeting && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Created: {new Date(meeting.created_at).toLocaleDateString()}
                  {meeting.updated_at && (
                    <span> • Last updated: {new Date(meeting.updated_at).toLocaleDateString()}</span>
                  )}
                </Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Saving...' : (meeting ? 'Update Meeting' : 'Create Meeting')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddMeeting;
